<template>
  <div v-if="visible" class="cookie-overlay">
    <div class="cookie-banner" role="dialog" aria-label="Cookie-Einstellungen">
      <!-- Main Banner -->
      <div v-if="!showSettings" class="cookie-main">
        <div class="cookie-text">
          <h3>Wir respektieren Ihre Privatsphäre</h3>
          <p>
            Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Nutzung
            unserer Website zu ermöglichen. Einige davon sind technisch notwendig, andere helfen
            uns, die Website zu verbessern und Ihnen personalisierte Inhalte anzubieten.
            Weitere Informationen finden Sie in unserer
            <router-link to="/datenschutz" @click="visible = false">Datenschutzerklärung</router-link>.
          </p>
        </div>
        <div class="cookie-actions">
          <button class="btn btn-neutral" @click="handleAcceptAll">Alle akzeptieren</button>
          <button class="btn btn-neutral" @click="handleRejectAll">Alle ablehnen</button>
          <button class="btn btn-link" @click="showSettings = true">Einstellungen</button>
        </div>
      </div>

      <!-- Settings Panel -->
      <div v-else class="cookie-settings">
        <div class="settings-header">
          <h3>Cookie-Einstellungen</h3>
          <p>Hier können Sie auswählen, welche Kategorien von Cookies Sie zulassen möchten.</p>
        </div>

        <div class="settings-categories">
          <div class="category">
            <div class="category-header">
              <div class="category-info">
                <strong>Notwendig</strong>
                <span class="always-on">Immer aktiv</span>
              </div>
              <label class="toggle disabled">
                <input type="checkbox" checked disabled />
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="category-desc">
              Diese Cookies sind für den Betrieb der Website unbedingt erforderlich
              und können nicht deaktiviert werden.
            </p>
          </div>

          <div class="category">
            <div class="category-header">
              <div class="category-info">
                <strong>Analyse</strong>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="settings.analytics" />
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="category-desc">
              Helfen uns zu verstehen, wie Besucher mit der Website interagieren,
              um das Nutzererlebnis zu verbessern. (HubSpot Analytics)
            </p>
          </div>

          <div class="category">
            <div class="category-header">
              <div class="category-info">
                <strong>Marketing</strong>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="settings.marketing" />
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="category-desc">
              Werden verwendet, um Ihnen relevante Werbung und personalisierte
              Inhalte anzuzeigen. (Meta Pixel, Google Ads)
            </p>
          </div>

          <div class="category">
            <div class="category-header">
              <div class="category-info">
                <strong>Externe Medien</strong>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="settings.externalMedia" />
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="category-desc">
              Inhalte von externen Anbietern wie Google Maps werden erst nach
              Ihrer Zustimmung geladen.
            </p>
          </div>
        </div>

        <div class="settings-actions">
          <button class="btn btn-neutral" @click="handleSaveSettings">Auswahl speichern</button>
          <button class="btn btn-neutral" @click="handleAcceptAll">Alle akzeptieren</button>
          <button class="btn btn-link" @click="showSettings = false">Zurück</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useConsentManager } from '@/composables/useConsentManager';

export default {
  name: 'CookieBanner',
  data() {
    return {
      visible: false,
      showSettings: false,
      settings: {
        analytics: false,
        marketing: false,
        externalMedia: false,
      },
    };
  },
  methods: {
    handleAcceptAll() {
      const { acceptAll } = useConsentManager();
      acceptAll();
      this.visible = false;
      this.$emit('consent-updated');
    },
    handleRejectAll() {
      const { rejectAll } = useConsentManager();
      rejectAll();
      this.visible = false;
      this.$emit('consent-updated');
    },
    handleSaveSettings() {
      const { setConsent } = useConsentManager();
      setConsent({ ...this.settings });
      this.visible = false;
      this.$emit('consent-updated');
    },
    open(settingsView = false) {
      const { state } = useConsentManager();
      this.settings = {
        analytics: state.consent.analytics,
        marketing: state.consent.marketing,
        externalMedia: state.consent.externalMedia,
      };
      this.showSettings = settingsView;
      this.visible = true;
    },
  },
  mounted() {
    const { state } = useConsentManager();
    if (!state.decided) {
      this.visible = true;
    }
  },
};
</script>

<style scoped>
.cookie-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
}

.cookie-banner {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.15);
  max-width: 720px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 32px;
}

.cookie-text h3 {
  font-size: 1.2em;
  margin: 0 0 12px;
  color: var(--color-secondary);
}

.cookie-text p {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.92em;
  line-height: 1.7;
}

.cookie-text a {
  color: var(--color-primary);
  text-decoration: underline;
}

.cookie-actions,
.settings-actions {
  display: flex;
  gap: 10px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.92em;
  font-family: inherit;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-neutral {
  background: var(--color-background-alt);
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-neutral:hover {
  background: var(--color-border);
}

.btn-link {
  background: transparent;
  color: var(--color-text-muted);
  padding: 12px 16px;
}

.btn-link:hover {
  color: var(--color-primary);
}

/* Settings Panel */
.settings-header h3 {
  font-size: 1.2em;
  margin: 0 0 8px;
  color: var(--color-secondary);
}

.settings-header p {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.92em;
  line-height: 1.6;
}

.settings-categories {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.category {
  padding: 16px;
  background: var(--color-background-alt);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-border);
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.category-info strong {
  font-size: 0.95em;
  color: var(--color-secondary);
}

.always-on {
  display: inline-block;
  margin-left: 8px;
  font-size: 0.8em;
  color: var(--color-primary);
  background: var(--color-primary-light);
  padding: 2px 8px;
  border-radius: 100px;
  font-weight: 500;
}

.category-desc {
  margin: 8px 0 0;
  font-size: 0.85em;
  color: var(--color-text-muted);
  line-height: 1.6;
}

/* Toggle Switch */
.toggle {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  flex-shrink: 0;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--color-border);
  transition: 0.3s;
  border-radius: 26px;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle input:checked + .toggle-slider {
  background-color: var(--color-primary);
}

.toggle input:checked + .toggle-slider::before {
  transform: translateX(22px);
}

.toggle.disabled .toggle-slider {
  background-color: var(--color-primary);
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle.disabled .toggle-slider::before {
  transform: translateX(22px);
}

@media (max-width: 768px) {
  .cookie-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .cookie-banner {
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    padding: 24px;
    max-height: 90vh;
  }

  .cookie-actions,
  .settings-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    text-align: center;
  }
}
</style>
